// v4
// Path: frontend/src/components/SyncHub/ConnectionBindingDrawer.jsx
// Inputs: isOpen, onClose, editingConnection, targetContainer, onSaveSuccess, authHeaders, apps, driverTypes, authModels, integrationAccounts, onNotification
// Outputs: Compact, high-density side drawer component for creating, editing, and binding credentials to infrastructure targets (Host -> Interface -> Instance -> Container)
// Summary: High-density, anti-card drawer with network interface selection, instance-level credential inheritance, dropdown-first credential selection with inline subform, and toast feedback.

import React, { useState, useEffect, useMemo } from 'react';

export default function ConnectionBindingDrawer({
    isOpen,
    onClose,
    editingConnection,
    targetContainer,
    onSaveSuccess,
    authHeaders,
    apps = [],
    driverTypes = [],
    authModels = [],
    integrationAccounts = [],
    onNotification
}) {
    // Top-level topology state
    const [topology, setTopology] = useState({
        organizations: [],
        hosts: [],
        instances: [],
        containers: [],
        authModels: [],
        credentials: [],
        driverTypes: [],
        interfaces: [],
        interfaceTypes: []
    });

    // Selected Interface State
    const [selectedInterfaceId, setSelectedInterfaceId] = useState('');
    const [isAddingInterface, setIsAddingInterface] = useState(false);
    const [isEditingInterface, setIsEditingInterface] = useState(false);
    const [newInterfaceForm, setNewInterfaceForm] = useState({
        EndpointAddress: '',
        InterfaceTypeID: 3,
        NetworkIdentifier: '',
        PriorityOrder: 10,
        Notes: ''
    });
    const [loadingTopology, setLoadingTopology] = useState(false);

    // Selected Target IDs
    const [selectedHostId, setSelectedHostId] = useState('');
    const [selectedInstanceId, setSelectedInstanceId] = useState('');
    const [selectedContainerId, setSelectedContainerId] = useState('');

    // Selected Credential & Binding Details
    const [selectedCredentialId, setSelectedCredentialId] = useState('');
    const [bindingName, setBindingName] = useState('');
    const [bindingNotes, setBindingNotes] = useState('');
    const [isDefault, setIsDefault] = useState(true);
    const [isActive, setIsActive] = useState(true);

    // Inline "Add / Edit" Toggles
    const [isAddingHost, setIsAddingHost] = useState(false);
    const [isEditingHost, setIsEditingHost] = useState(false);
    const [isAddingInstance, setIsAddingInstance] = useState(false);
    const [isEditingInstance, setIsEditingInstance] = useState(false);
    const [isAddingContainer, setIsAddingContainer] = useState(false);
    const [isEditingContainer, setIsEditingContainer] = useState(false);
    const [isAddingOrg, setIsAddingOrg] = useState(false);
    const [isAddingAuthModel, setIsAddingAuthModel] = useState(false);
    const [isEditingCredential, setIsEditingCredential] = useState(false);

    // Inline Form States
    const [newHostForm, setNewHostForm] = useState({ HostName: '', DisplayName: '', HostType: 'ON_PREM_SERVER', OrganizationID: '' });
    const [newInstanceForm, setNewInstanceForm] = useState({ ServiceName: '', EngineType: 'SQL Server', ListenPort: 1433, OrganizationID: '' });
    const [newContainerForm, setNewContainerForm] = useState({ ContainerName: '', ContainerType: 'RELATIONAL_DATABASE', ContainerRole: 'PRODUCTION', OrganizationID: '' });
    const [newOrgForm, setNewOrgForm] = useState({ OrganizationName: '', OrganizationType: 'Store', ParentOrganizationID: '' });
    const [newAuthModelForm, setNewAuthModelForm] = useState({ AuthModelCode: '', AuthModelName: '', Description: '' });

    // New Credential Form State
    const [newCredForm, setNewCredForm] = useState({
        CredentialName: '',
        SourceTypeID: 1,
        AuthModelID: 3,
        Username: 'sa',
        password: '',
        realmId: '',
        clientId: '',
        clientSecret: '',
        accessToken: '',
        refreshToken: '',
        environment: 'sandbox',
        baseUrl: 'https://sandbox-quickbooks.api.intuit.com',
        apiKey: '',
        headerName: 'Authorization',
        domain: '',
        notes: ''
    });

    // Test Connection & Submission State
    const [testingConnection, setTestingConnection] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Fetch Topology on open
    const loadTopology = async () => {
        setLoadingTopology(true);
        try {
            const res = await fetch('/api/catalog/topology', { headers: authHeaders });
            const data = await res.json();
            if (data.success && data.data) {
                setTopology(data.data);
            }
        } catch (err) {
            console.error('Failed to load topology:', err);
        } finally {
            setLoadingTopology(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadTopology();
        }
    }, [isOpen]);

    // Initialize selection states when editingConnection or targetContainer changes
    useEffect(() => {
        if (!isOpen) return;

        setTestResult(null);
        setIsAddingHost(false);
        setIsEditingHost(false);
        setIsAddingInstance(false);
        setIsEditingInstance(false);
        setIsAddingContainer(false);
        setIsEditingContainer(false);
        setIsAddingOrg(false);
        setIsAddingAuthModel(false);
        setIsEditingCredential(false);

        if (editingConnection) {
            setSelectedHostId(editingConnection.HostID ? String(editingConnection.HostID) : '');
            setSelectedInstanceId(editingConnection.InstanceID ? String(editingConnection.InstanceID) : '');
            setSelectedContainerId(editingConnection.ContainerID ? String(editingConnection.ContainerID) : '');
            setSelectedCredentialId(editingConnection.CredentialID ? String(editingConnection.CredentialID) : '');
            setBindingName(editingConnection.BindingName || editingConnection.CredentialName || editingConnection.ContainerName || '');
            setBindingNotes(editingConnection.BindingNotes || '');
            setIsDefault(editingConnection.IsDefault !== undefined ? !!editingConnection.IsDefault : true);
            setIsActive(editingConnection.BindingIsActive !== undefined ? !!editingConnection.BindingIsActive : true);

            setNewCredForm({
                CredentialName: editingConnection.CredentialName || '',
                SourceTypeID: editingConnection.SourceTypeID || 1,
                AuthModelID: editingConnection.AuthModelID || 3,
                Username: editingConnection.Username || 'sa',
                password: '',
                realmId: editingConnection.realmId || editingConnection.DatabaseName || '',
                clientId: editingConnection.clientId || '',
                clientSecret: '',
                accessToken: '',
                refreshToken: '',
                environment: editingConnection.environment || 'sandbox',
                baseUrl: editingConnection.baseUrl || editingConnection.HostServer || 'https://sandbox-quickbooks.api.intuit.com',
                apiKey: '',
                headerName: 'Authorization',
                domain: editingConnection.rawConfig?.domain || '',
                notes: editingConnection.CredentialNotes || ''
            });
        } else if (targetContainer) {
            setSelectedHostId(targetContainer.HostID ? String(targetContainer.HostID) : '');
            setSelectedInstanceId(targetContainer.InstanceID ? String(targetContainer.InstanceID) : '');
            setSelectedContainerId(targetContainer.ContainerID ? String(targetContainer.ContainerID) : '');
            setSelectedCredentialId(targetContainer.CredentialID ? String(targetContainer.CredentialID) : '');
            setBindingName(targetContainer.BindingName || `${targetContainer.ContainerName} Binding`);
            setBindingNotes(targetContainer.BindingNotes || '');
            setIsDefault(true);
            setIsActive(true);

            setNewCredForm(prev => ({
                ...prev,
                CredentialName: `${targetContainer.ContainerName} Access Credential`,
                realmId: targetContainer.ContainerName || ''
            }));
        } else {
            setSelectedHostId('');
            setSelectedInstanceId('');
            setSelectedContainerId('');
            setSelectedCredentialId('');
            setBindingName('');
            setBindingNotes('');
            setIsDefault(true);
            setIsActive(true);
            setNewCredForm({
                CredentialName: '',
                SourceTypeID: 1,
                AuthModelID: 3,
                Username: 'sa',
                password: '',
                realmId: '',
                clientId: '',
                clientSecret: '',
                accessToken: '',
                refreshToken: '',
                environment: 'sandbox',
                baseUrl: 'https://sandbox-quickbooks.api.intuit.com',
                apiKey: '',
                headerName: 'Authorization',
                domain: '',
                notes: ''
            });
        }
    }, [isOpen, editingConnection, targetContainer]);

    // Close on Escape key (The Light Dismiss Rule)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Filter instances by selected Host
    const availableInstances = useMemo(() => {
        if (!selectedHostId) return topology.instances;
        return topology.instances.filter(i => String(i.HostID) === String(selectedHostId));
    }, [topology.instances, selectedHostId]);

    // Filter containers by selected Instance
    const availableContainers = useMemo(() => {
        if (!selectedInstanceId) {
            if (!selectedHostId) return topology.containers;
            const validInstIds = new Set(availableInstances.map(i => String(i.InstanceID)));
            return topology.containers.filter(c => validInstIds.has(String(c.InstanceID)));
        }
        return topology.containers.filter(c => String(c.InstanceID) === String(selectedInstanceId));
    }, [topology.containers, selectedInstanceId, selectedHostId, availableInstances]);

    // Determine current effective organization name and source
    const effectiveOrgInfo = useMemo(() => {
        const currentContainer = topology.containers.find(c => String(c.ContainerID) === String(selectedContainerId));
        const currentInstance = topology.instances.find(i => String(i.InstanceID) === String(selectedInstanceId || currentContainer?.InstanceID));
        const currentHost = topology.hosts.find(h => String(h.HostID) === String(selectedHostId || currentInstance?.HostID));

        if (currentContainer?.OrganizationID) {
            const org = topology.organizations.find(o => o.OrganizationID === currentContainer.OrganizationID);
            return { name: org?.OrganizationName || `Org #${currentContainer.OrganizationID}`, level: 'Container Override' };
        }
        if (currentInstance?.OrganizationID) {
            const org = topology.organizations.find(o => o.OrganizationID === currentInstance.OrganizationID);
            return { name: org?.OrganizationName || `Org #${currentInstance.OrganizationID}`, level: 'Instance Override' };
        }
        if (currentHost?.OrganizationID) {
            const org = topology.organizations.find(o => o.OrganizationID === currentHost.OrganizationID);
            return { name: org?.OrganizationName || `Org #${currentHost.OrganizationID}`, level: 'Inherited from Host' };
        }
        return { name: 'Default Organization', level: 'Unassigned' };
    }, [topology, selectedContainerId, selectedInstanceId, selectedHostId]);

    // Selected credential object
    const selectedCredObj = useMemo(() => {
        if (!selectedCredentialId || selectedCredentialId === '__NEW__') return null;
        return topology.credentials.find(c => String(c.CredentialID) === String(selectedCredentialId));
    }, [topology.credentials, selectedCredentialId]);

    // Determine active AuthModel
    const activeAuthModel = useMemo(() => {
        if (selectedCredObj) {
            return topology.authModels.find(am => am.AuthModelID === selectedCredObj.AuthModelID);
        }
        return topology.authModels.find(am => am.AuthModelID === parseInt(newCredForm.AuthModelID, 10));
    }, [selectedCredObj, topology.authModels, newCredForm.AuthModelID]);

    const isOAuth = activeAuthModel?.AuthModelCode === 'OAUTH2' || newCredForm.SourceTypeID === 2;
    const isApiKey = activeAuthModel?.AuthModelCode === 'API_KEY';
    const isWindowsAuth = activeAuthModel?.AuthModelCode === 'WINDOWS_AUTH' || activeAuthModel?.AuthModelCode === 'NTLM';
    const isSqlLogin = !isOAuth && !isApiKey && !isWindowsAuth;

    // Handlers for inline entity creation with auto-selection
    const handleCreateOrganization = async (e) => {
        if (e) e.preventDefault();
        if (!newOrgForm.OrganizationName.trim()) return;
        try {
            const res = await fetch('/api/catalog/organizations', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(newOrgForm)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const createdOrg = data.data;
                const createdOrgId = String(createdOrg.OrganizationID);
                setTopology(prev => ({
                    ...prev,
                    organizations: [...prev.organizations, createdOrg].sort((a, b) => a.OrganizationName.localeCompare(b.OrganizationName))
                }));
                if (isAddingHost || isEditingHost) {
                    setNewHostForm(prev => ({ ...prev, OrganizationID: createdOrgId }));
                } else if (isAddingInstance || isEditingInstance) {
                    setNewInstanceForm(prev => ({ ...prev, OrganizationID: createdOrgId }));
                } else if (isAddingContainer || isEditingContainer) {
                    setNewContainerForm(prev => ({ ...prev, OrganizationID: createdOrgId }));
                }
                setIsAddingOrg(false);
                setNewOrgForm({ OrganizationName: '', OrganizationType: 'Store', ParentOrganizationID: '' });
                if (onNotification) onNotification(`Created Organization "${createdOrg.OrganizationName}"`);
            } else {
                alert(data.error || 'Failed to create organization');
            }
        } catch (err) {
            alert('Error creating organization: ' + err.message);
        }
    };

    const handleCreateHost = async (e) => {
        if (e) e.preventDefault();
        if (!newHostForm.HostName.trim()) return;
        try {
            const res = await fetch('/api/catalog/hosts', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(newHostForm)
            });
            const data = await res.json();
            if (data.success && data.data) {
                setTopology(prev => ({
                    ...prev,
                    hosts: [...prev.hosts, data.data].sort((a, b) => (a.DisplayName || a.HostName).localeCompare(b.DisplayName || b.HostName))
                }));
                setSelectedHostId(String(data.data.HostID));
                setIsAddingHost(false);
                setIsEditingHost(false);
                setNewHostForm({ HostName: '', DisplayName: '', HostType: 'ON_PREM_SERVER', OrganizationID: '' });
                if (onNotification) onNotification(`Created Host "${data.data.DisplayName || data.data.HostName}"`);
            } else {
                alert(data.error || 'Failed to create host');
            }
        } catch (err) {
            alert('Error creating host: ' + err.message);
        }
    };

    const handleCreateInstance = async (e) => {
        if (e) e.preventDefault();
        if (!newInstanceForm.ServiceName.trim() || !selectedHostId) {
            alert('Please select a Host and specify a Service Name');
            return;
        }
        try {
            const res = await fetch('/api/catalog/instances', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    ...newInstanceForm,
                    HostID: parseInt(selectedHostId, 10)
                })
            });
            const data = await res.json();
            if (data.success && data.data) {
                setTopology(prev => ({
                    ...prev,
                    instances: [...prev.instances, data.data].sort((a, b) => a.ServiceName.localeCompare(b.ServiceName))
                }));
                setSelectedInstanceId(String(data.data.InstanceID));
                setIsAddingInstance(false);
                setIsEditingInstance(false);
                setNewInstanceForm({ ServiceName: '', EngineType: 'SQL Server', ListenPort: 1433, OrganizationID: '' });
                if (onNotification) onNotification(`Created Service Instance "${data.data.ServiceName}"`);
            } else {
                alert(data.error || 'Failed to create instance');
            }
        } catch (err) {
            alert('Error creating instance: ' + err.message);
        }
    };

    const handleCreateContainer = async (e) => {
        if (e) e.preventDefault();
        if (!newContainerForm.ContainerName.trim() || !selectedInstanceId) {
            alert('Please select an Instance and specify a Container Name');
            return;
        }
        try {
            const res = await fetch('/api/catalog/containers', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    ...newContainerForm,
                    InstanceID: parseInt(selectedInstanceId, 10)
                })
            });
            const data = await res.json();
            if (data.success && data.data) {
                setTopology(prev => ({
                    ...prev,
                    containers: [...prev.containers, data.data].sort((a, b) => a.ContainerName.localeCompare(b.ContainerName))
                }));
                setSelectedContainerId(String(data.data.ContainerID));
                setIsAddingContainer(false);
                setIsEditingContainer(false);
                setNewContainerForm({ ContainerName: '', ContainerType: 'RELATIONAL_DATABASE', ContainerRole: 'PRODUCTION', OrganizationID: '' });
                if (onNotification) onNotification(`Created Container "${data.data.ContainerName}"`);
            } else {
                alert(data.error || 'Failed to create container');
            }
        } catch (err) {
            alert('Error creating container: ' + err.message);
        }
    };

    const handleCreateAuthModel = async (e) => {
        if (e) e.preventDefault();
        if (!newAuthModelForm.AuthModelCode.trim() || !newAuthModelForm.AuthModelName.trim()) return;
        try {
            const res = await fetch('/api/catalog/auth-models', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(newAuthModelForm)
            });
            const data = await res.json();
            if (data.success && data.data) {
                setTopology(prev => ({
                    ...prev,
                    authModels: [...prev.authModels, data.data].sort((a, b) => a.AuthModelName.localeCompare(b.AuthModelName))
                }));
                setNewCredForm(prev => ({ ...prev, AuthModelID: data.data.AuthModelID }));
                setIsAddingAuthModel(false);
                setNewAuthModelForm({ AuthModelCode: '', AuthModelName: '', Description: '' });
                if (onNotification) onNotification(`Created Auth Model "${data.data.AuthModelName}"`);
            } else {
                alert(data.error || 'Failed to create auth model');
            }
        } catch (err) {
            alert('Error creating auth model: ' + err.message);
        }
    };

    const handleCreateCredential = async (e) => {
        if (e) e.preventDefault();
        if (!newCredForm.CredentialName.trim()) {
            alert('Please specify a Credential Name');
            return;
        }
        try {
            const payload = {
                CredentialName: newCredForm.CredentialName.trim(),
                AuthModelID: newCredForm.AuthModelID,
                SourceTypeID: newCredForm.SourceTypeID,
                Username: newCredForm.Username,
                password: newCredForm.password,
                EncryptedPassword: newCredForm.password,
                Notes: newCredForm.notes || undefined,
                config: {
                    baseUrl: newCredForm.baseUrl,
                    realmId: newCredForm.realmId,
                    clientId: newCredForm.clientId,
                    clientSecret: newCredForm.clientSecret,
                    accessToken: newCredForm.accessToken,
                    refreshToken: newCredForm.refreshToken,
                    apiKey: newCredForm.apiKey,
                    headerName: newCredForm.headerName,
                    domain: newCredForm.domain
                }
            };
            const res = await fetch('/api/catalog/connections', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const newId = data.data.CredentialID || data.data.id;
                const newCred = {
                    ...data.data,
                    CredentialID: newId,
                    CredentialName: newCredForm.CredentialName,
                    Username: newCredForm.Username,
                    AuthModelID: newCredForm.AuthModelID
                };
                setTopology(prev => ({
                    ...prev,
                    credentials: [...prev.credentials.filter(c => c.CredentialID !== newId), newCred].sort((a, b) => a.CredentialName.localeCompare(b.CredentialName))
                }));
                setSelectedCredentialId(String(newId));
                setIsEditingCredential(false);
                if (onNotification) onNotification(`Created and selected Credential "${newCred.CredentialName}"`);
            } else {
                alert(data.error || 'Failed to create credential');
            }
        } catch (err) {
            alert('Error creating credential: ' + err.message);
        }
    };

    // Edit & Update Entity Handlers
    const handleStartEditHost = () => {
        const h = topology.hosts.find(x => String(x.HostID) === String(selectedHostId));
        if (!h) return;
        setNewHostForm({
            HostName: h.HostName || '',
            DisplayName: h.DisplayName || '',
            HostType: h.HostType || 'ON_PREM_SERVER',
            OrganizationID: h.OrganizationID ? String(h.OrganizationID) : ''
        });
        setIsAddingHost(false);
        setIsEditingHost(true);
    };

    const handleUpdateHost = async (e) => {
        if (e) e.preventDefault();
        if (!newHostForm.HostName.trim() || !selectedHostId) return;
        try {
            const res = await fetch(`/api/catalog/hosts/${selectedHostId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(newHostForm)
            });
            const data = await res.json();
            if (data.success && data.data) {
                setTopology(prev => ({
                    ...prev,
                    hosts: prev.hosts.map(h => String(h.HostID) === String(selectedHostId) ? { ...h, ...data.data } : h)
                }));
                setIsEditingHost(false);
                if (onNotification) onNotification(`Updated Host "${data.data.DisplayName || data.data.HostName}"`);
            } else {
                alert(data.error || 'Failed to update host');
            }
        } catch (err) {
            alert('Error updating host: ' + err.message);
        }
    };

    const handleStartEditInstance = () => {
        const inst = topology.instances.find(x => String(x.InstanceID) === String(selectedInstanceId));
        if (!inst) return;
        setNewInstanceForm({
            ServiceName: inst.ServiceName || '',
            EngineType: inst.EngineType || 'SQL Server',
            ListenPort: inst.ListenPort || 1433,
            OrganizationID: inst.OrganizationID ? String(inst.OrganizationID) : ''
        });
        setIsAddingInstance(false);
        setIsEditingInstance(true);
    };

    const handleUpdateInstance = async (e) => {
        if (e) e.preventDefault();
        if (!newInstanceForm.ServiceName.trim() || !selectedInstanceId) return;
        try {
            const res = await fetch(`/api/catalog/instances/${selectedInstanceId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(newInstanceForm)
            });
            const data = await res.json();
            if (data.success && data.data) {
                setTopology(prev => ({
                    ...prev,
                    instances: prev.instances.map(i => String(i.InstanceID) === String(selectedInstanceId) ? { ...i, ...data.data } : i)
                }));
                setIsEditingInstance(false);
                if (onNotification) onNotification(`Updated Service Instance "${data.data.ServiceName}"`);
            } else {
                alert(data.error || 'Failed to update instance');
            }
        } catch (err) {
            alert('Error updating instance: ' + err.message);
        }
    };

    const handleStartEditContainer = () => {
        const cont = topology.containers.find(x => String(x.ContainerID) === String(selectedContainerId));
        if (!cont) return;
        setNewContainerForm({
            ContainerName: cont.ContainerName || '',
            ContainerType: cont.ContainerType || 'RELATIONAL_DATABASE',
            ContainerRole: cont.ContainerRole || 'PRODUCTION',
            OrganizationID: cont.OrganizationID ? String(cont.OrganizationID) : ''
        });
        setIsAddingContainer(false);
        setIsEditingContainer(true);
    };

    const handleUpdateContainer = async (e) => {
        if (e) e.preventDefault();
        if (!newContainerForm.ContainerName.trim() || !selectedContainerId) return;
        try {
            const res = await fetch(`/api/catalog/containers/${selectedContainerId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(newContainerForm)
            });
            const data = await res.json();
            if (data.success && data.data) {
                setTopology(prev => ({
                    ...prev,
                    containers: prev.containers.map(c => String(c.ContainerID) === String(selectedContainerId) ? { ...c, ...data.data } : c)
                }));
                setIsEditingContainer(false);
                if (onNotification) onNotification(`Updated Container "${data.data.ContainerName}"`);
            } else {
                alert(data.error || 'Failed to update container');
            }
        } catch (err) {
            alert('Error updating container: ' + err.message);
        }
    };

    const handleStartEditCredential = () => {
        if (!selectedCredObj) return;
        setNewCredForm({
            CredentialName: selectedCredObj.CredentialName || '',
            SourceTypeID: selectedCredObj.SourceTypeID || 1,
            AuthModelID: selectedCredObj.AuthModelID || 3,
            Username: selectedCredObj.Username || '',
            password: '',
            realmId: selectedCredObj.realmId || selectedCredObj.DatabaseName || '',
            clientId: selectedCredObj.clientId || '',
            clientSecret: '',
            accessToken: '',
            refreshToken: '',
            environment: selectedCredObj.environment || 'sandbox',
            baseUrl: selectedCredObj.baseUrl || selectedCredObj.HostServer || 'https://sandbox-quickbooks.api.intuit.com',
            apiKey: '',
            headerName: 'Authorization',
            domain: selectedCredObj.rawConfig?.domain || '',
            notes: selectedCredObj.Notes || ''
        });
        setIsEditingCredential(true);
    };

    const handleUpdateCredential = async (e) => {
        if (e) e.preventDefault();
        if (!selectedCredentialId || selectedCredentialId === '__NEW__') return;
        try {
            const payload = {
                CredentialName: newCredForm.CredentialName.trim() || undefined,
                AuthModelID: newCredForm.AuthModelID,
                SourceTypeID: newCredForm.SourceTypeID,
                Username: newCredForm.Username,
                password: newCredForm.password || undefined,
                EncryptedPassword: newCredForm.password || undefined,
                Notes: newCredForm.notes || undefined,
                config: {
                    baseUrl: newCredForm.baseUrl,
                    realmId: newCredForm.realmId,
                    clientId: newCredForm.clientId,
                    clientSecret: newCredForm.clientSecret,
                    accessToken: newCredForm.accessToken,
                    refreshToken: newCredForm.refreshToken,
                    apiKey: newCredForm.apiKey,
                    headerName: newCredForm.headerName,
                    domain: newCredForm.domain
                }
            };
            const res = await fetch(`/api/catalog/connections/${selectedCredentialId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                setTopology(prev => ({
                    ...prev,
                    credentials: prev.credentials.map(c => String(c.CredentialID) === String(selectedCredentialId)
                        ? { ...c, CredentialName: newCredForm.CredentialName, Username: newCredForm.Username, AuthModelID: newCredForm.AuthModelID }
                        : c
                    )
                }));
                setIsEditingCredential(false);
                if (onNotification) onNotification(`Updated Credential "${newCredForm.CredentialName}"`);
            } else {
                alert(data.error || 'Failed to update credential');
            }
        } catch (err) {
            alert('Error updating credential: ' + err.message);
        }
    };

    // Test Connection Handler (Strict Explicit Targeting, Zero Fallbacks)
    const handleTest = async () => {
        setTestingConnection(true);
        setTestResult(null);
        try {
            const selectedHost = topology.hosts.find(h => String(h.HostID) === String(selectedHostId));
            const selectedInst = topology.instances.find(i => String(i.InstanceID) === String(selectedInstanceId));
            const selectedCont = topology.containers.find(c => String(c.ContainerID) === String(selectedContainerId));

            if (!isOAuth) {
                if (!selectedHost?.HostName) {
                    alert('Please select a Host Server to test this connection.');
                    setTestingConnection(false);
                    return;
                }
                if (!selectedCont?.ContainerName) {
                    alert('Please select a Data Container to test this connection.');
                    setTestingConnection(false);
                    return;
                }
            }

            const selectedIface = (topology.interfaces || []).find(i => String(i.InterfaceID) === String(selectedInterfaceId));
            const effectiveHost = isOAuth ? newCredForm.baseUrl : (selectedIface?.EndpointAddress || selectedHost.HostName);
            const dbName = isOAuth ? newCredForm.realmId : (selectedCont?.ContainerName || 'master');

            const testPayload = {
                SourceTypeID: newCredForm.SourceTypeID,
                AuthModelID: newCredForm.AuthModelID,
                HostServer: effectiveHost,
                HostPort: isOAuth ? 443 : (selectedInst?.ListenPort || 1433),
                DatabaseName: dbName,
                Username: newCredForm.Username,
                password: newCredForm.password,
                config: {
                    baseUrl: newCredForm.baseUrl,
                    realmId: newCredForm.realmId,
                    clientId: newCredForm.clientId,
                    clientSecret: newCredForm.clientSecret,
                    accessToken: newCredForm.accessToken,
                    refreshToken: newCredForm.refreshToken,
                    apiKey: newCredForm.apiKey,
                    headerName: newCredForm.headerName,
                    domain: newCredForm.domain
                }
            };

            const url = selectedCredentialId && selectedCredentialId !== '__NEW__' 
                ? `/api/catalog/connections/${selectedCredentialId}/test`
                : '/api/catalog/connections/test';

            const res = await fetch(url, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(testPayload)
            });
            const data = await res.json();
            setTestResult(data);
            if (onNotification) {
                onNotification(
                    data.success ? `Connection verified! (${data.latencyMs || data.latency || 0}ms)` : `Connection test failed: ${data.message || data.error || 'Unreachable'}`,
                    data.success ? 'success' : 'error'
                );
            }
        } catch (err) {
            setTestResult({ success: false, message: err.message });
            if (onNotification) {
                onNotification(`Connection test error: ${err.message}`, 'error');
            }
        } finally {
            setTestingConnection(false);
        }
    };

    // Save / Submit Binding
    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const isEdit = !!editingConnection;
            const isNewCred = selectedCredentialId === '__NEW__' || !selectedCredentialId;

            const isInstanceBinding = selectedContainerId === '__INSTANCE__' || (!selectedContainerId && selectedInstanceId);
            const targetContId = (selectedContainerId === '__INSTANCE__' || !selectedContainerId) ? null : parseInt(selectedContainerId, 10);
            const targetInstId = selectedInstanceId ? parseInt(selectedInstanceId, 10) : null;

            if (!targetContId && !targetInstId && !isAddingContainer && !isAddingInstance) {
                alert('Please select a Target Data Container or Service Instance.');
                setSaving(false);
                return;
            }

            const payload = {
                ContainerID: targetContId,
                InstanceID: targetInstId,
                HostID: selectedHostId ? parseInt(selectedHostId, 10) : null,
                CredentialID: !isNewCred ? parseInt(selectedCredentialId, 10) : null,
                BindingID: editingConnection?.BindingID || null,
                BindingName: bindingName.trim() || newCredForm.CredentialName || (isInstanceBinding ? 'Instance Binding' : 'Container Binding'),
                BindingNotes: bindingNotes.trim() || null,
                IsDefault: false,
                IsActive: isActive,
                // Credential creation fields if new credential
                newCredential: isNewCred,
                CredentialName: newCredForm.CredentialName.trim() || bindingName.trim() || 'New Credential',
                SourceTypeID: newCredForm.SourceTypeID,
                AuthModelID: newCredForm.AuthModelID,
                Username: newCredForm.Username,
                password: newCredForm.password,
                EncryptedPassword: newCredForm.password,
                config: {
                    baseUrl: newCredForm.baseUrl,
                    realmId: newCredForm.realmId,
                    clientId: newCredForm.clientId,
                    clientSecret: newCredForm.clientSecret,
                    accessToken: newCredForm.accessToken,
                    refreshToken: newCredForm.refreshToken,
                    apiKey: newCredForm.apiKey,
                    headerName: newCredForm.headerName,
                    domain: newCredForm.domain
                }
            };

            const url = isEdit && editingConnection?.CredentialID && !isNewCred
                ? `/api/catalog/connections/${editingConnection.CredentialID}`
                : (targetContId 
                    ? `/api/catalog/containers/${targetContId}/bind`
                    : (targetInstId
                        ? `/api/catalog/instances/${targetInstId}/bind`
                        : '/api/catalog/connections'));

            const method = (isEdit && editingConnection?.CredentialID && !isNewCred) ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (data.success) {
                if (onNotification) onNotification(`Connection binding saved successfully!`);
                onSaveSuccess();
                onClose();
            } else {
                alert(data.error || 'Failed to save connection binding');
            }
        } catch (err) {
            alert('Error saving connection: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden flex justify-end bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose}>
            <div 
                className="w-full max-w-xl h-full bg-slate-950 border-l border-slate-800 shadow-2xl flex flex-col z-50 overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-4 py-2.5 bg-slate-900 border-b border-slate-800 sticky top-0 z-20 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-emerald-400 text-lg">hub</span>
                        <div>
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider">
                                {editingConnection ? 'Edit Connection & Binding' : (targetContainer ? `Bind Credential to ${targetContainer.ContainerName}` : 'New Connection Binding')}
                            </h3>
                            <p className="text-[10px] text-slate-400">
                                Connect target infrastructure (Host &rarr; Instance &rarr; Container) to credentials.
                            </p>
                        </div>
                    </div>
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                        title="Close (Esc)"
                    >
                        <span className="material-symbols-outlined text-base">close</span>
                    </button>
                </div>

                {loadingTopology ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 text-slate-400 gap-2">
                        <span className="material-symbols-outlined animate-spin text-2xl text-emerald-400">progress_activity</span>
                        <span className="text-xs">Loading infrastructure topology...</span>
                    </div>
                ) : (
                    <form onSubmit={handleSave} className="flex-1 p-3.5 flex flex-col gap-3 text-xs text-slate-300">
                        
                        {/* ========================================================================= */}
                        {/* SECTION 1: BINDING CONFIGURATION (DEFINES THE BINDING AT THE TOP) */}
                        {/* ========================================================================= */}
                        <div className="border-b border-slate-800/80 pb-3 space-y-2">
                            <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                <h4 className="text-[11px] font-bold text-amber-400 flex items-center gap-1.5 uppercase tracking-wider">
                                    <span className="material-symbols-outlined text-[14px]">link</span>
                                    1. Binding Configuration
                                </h4>
                                <div className="flex items-center gap-3">
                                    <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-slate-300 hover:text-white">
                                        <input
                                            type="checkbox"
                                            checked={isActive}
                                            onChange={e => setIsActive(e.target.checked)}
                                            className="rounded bg-slate-900 border-slate-700 text-emerald-500 focus:ring-0 w-3.5 h-3.5"
                                        />
                                        <span>Active</span>
                                    </label>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[10px] text-slate-400 block mb-0.5">Binding Name / Label</label>
                                    <input
                                        type="text"
                                        value={bindingName}
                                        onChange={e => setBindingName(e.target.value)}
                                        placeholder="e.g. Primary Production Binding"
                                        className="w-full bg-slate-900/90 border border-slate-700/80 rounded px-2 py-1 text-xs text-white"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] text-slate-400 block mb-0.5">Notes / Purpose</label>
                                    <input
                                        type="text"
                                        value={bindingNotes}
                                        onChange={e => setBindingNotes(e.target.value)}
                                        placeholder="Optional operational purpose..."
                                        className="w-full bg-slate-900/90 border border-slate-700/80 rounded px-2 py-1 text-xs text-white"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ========================================================================= */}
                        {/* SECTION 2: TARGET INFRASTRUCTURE (Host -> Instance -> Container) */}
                        {/* ========================================================================= */}
                        <div className="border-b border-slate-800/80 pb-3 space-y-2.5">
                            <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                <h4 className="text-[11px] font-bold text-emerald-400 flex items-center gap-1.5 uppercase tracking-wider">
                                    <span className="material-symbols-outlined text-[14px]">account_tree</span>
                                    2. Target Infrastructure
                                </h4>
                                <div className="text-[10px] flex items-center gap-1 text-slate-400">
                                    <span>Org:</span>
                                    <span className="text-white font-semibold">{effectiveOrgInfo.name}</span>
                                    <span className="text-[9px] text-emerald-400/90 bg-emerald-950/60 px-1 py-0.2 rounded border border-emerald-800/40">
                                        {effectiveOrgInfo.level}
                                    </span>
                                </div>
                            </div>

                            {/* Host Selector */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-slate-300 font-medium text-[11px]">Host Server / Device</label>
                                    <div className="flex items-center gap-2">
                                        {selectedHostId && !isAddingHost && !isEditingHost && (
                                            <button
                                                type="button"
                                                onClick={handleStartEditHost}
                                                className="text-[10px] text-sky-400 hover:text-sky-300 flex items-center gap-0.5 font-medium"
                                            >
                                                <span className="material-symbols-outlined text-[12px]">edit</span>
                                                Edit Selection
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (isAddingHost || isEditingHost) {
                                                    setIsAddingHost(false);
                                                    setIsEditingHost(false);
                                                } else {
                                                    setIsAddingHost(true);
                                                    setIsEditingHost(false);
                                                    setNewHostForm({ HostName: '', DisplayName: '', HostType: 'ON_PREM_SERVER', OrganizationID: '' });
                                                }
                                            }}
                                            className="text-[10px] text-emerald-400 hover:text-emerald-300 flex items-center gap-0.5 font-medium"
                                        >
                                            <span className="material-symbols-outlined text-[12px]">{(isAddingHost || isEditingHost) ? 'remove' : 'add'}</span>
                                            {(isAddingHost || isEditingHost) ? 'Cancel' : '+ Add New Host'}
                                        </button>
                                    </div>
                                </div>

                                {(isAddingHost || isEditingHost) ? (
                                    <div className="p-2.5 bg-slate-900/60 rounded border border-slate-800 space-y-2">
                                        <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                            <span className="text-[10px] font-bold text-emerald-300">
                                                {isEditingHost ? 'Edit Host Server' : 'Add New Host Server'}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => { setIsAddingHost(false); setIsEditingHost(false); }}
                                                className="text-slate-400 hover:text-white text-xs"
                                            >
                                                &times;
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Host Name (DNS / NetBIOS)</label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={newHostForm.HostName}
                                                    onChange={e => setNewHostForm({ ...newHostForm, HostName: e.target.value })}
                                                    placeholder="e.g. MIDSVR"
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs font-mono"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Display Name</label>
                                                <input
                                                    type="text"
                                                    value={newHostForm.DisplayName}
                                                    onChange={e => setNewHostForm({ ...newHostForm, DisplayName: e.target.value })}
                                                    placeholder="e.g. Midland Server"
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Host Type</label>
                                                <select
                                                    value={newHostForm.HostType}
                                                    onChange={e => setNewHostForm({ ...newHostForm, HostType: e.target.value })}
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                >
                                                    <option value="ON_PREM_SERVER">On-Prem Server</option>
                                                    <option value="CLOUD_PROVIDER">Cloud Provider / SaaS</option>
                                                    <option value="DESKTOP">Desktop Workstation</option>
                                                </select>
                                            </div>
                                            <div>
                                                <div className="flex items-center justify-between mb-0.5">
                                                    <label className="text-[10px] text-slate-400">Organization</label>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => setIsAddingOrg(!isAddingOrg)}
                                                        className="text-[9px] text-emerald-400 hover:underline"
                                                    >
                                                        + New Org
                                                    </button>
                                                </div>
                                                <select
                                                    value={newHostForm.OrganizationID}
                                                    onChange={e => setNewHostForm({ ...newHostForm, OrganizationID: e.target.value })}
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                >
                                                    <option value="">(None / Unassigned)</option>
                                                    {topology.organizations.map(o => (
                                                        <option key={o.OrganizationID} value={o.OrganizationID}>{o.OrganizationName}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex justify-end items-center gap-2 pt-1">
                                            <button
                                                type="button"
                                                onClick={() => { setIsAddingHost(false); setIsEditingHost(false); }}
                                                className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[11px] font-medium transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={isEditingHost ? handleUpdateHost : handleCreateHost}
                                                className="px-2.5 py-0.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-[11px] font-semibold transition-colors"
                                            >
                                                {isEditingHost ? 'Update Host' : 'Save Host'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <select
                                        value={selectedHostId}
                                        onChange={e => {
                                            setSelectedHostId(e.target.value);
                                            setSelectedInstanceId('');
                                            setSelectedContainerId('');
                                        }}
                                        className="w-full bg-slate-900/90 border border-slate-700/80 rounded px-2 py-1.5 text-white text-xs"
                                    >
                                        <option value="">-- Select Host Server --</option>
                                        {topology.hosts.map(h => {
                                            const org = topology.organizations.find(o => o.OrganizationID === h.OrganizationID);
                                            return (
                                                <option key={h.HostID} value={h.HostID}>
                                                    {h.DisplayName || h.HostName} ({h.HostName}) {org ? `[${org.OrganizationName}]` : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                )}
                            </div>

                            {/* Inline New Organization Subform */}
                            {isAddingOrg && (
                                <div className="p-2.5 bg-slate-900/80 rounded border border-purple-900/60 space-y-2">
                                    <div className="flex items-center justify-between pb-1 border-b border-purple-900/40">
                                        <span className="text-[10px] font-bold text-purple-300">Add New Organization</span>
                                        <button type="button" onClick={() => setIsAddingOrg(false)} className="text-slate-400 hover:text-white">&times;</button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[10px] text-slate-400 block mb-0.5">Organization Name</label>
                                            <input
                                                type="text"
                                                required
                                                value={newOrgForm.OrganizationName}
                                                onChange={e => setNewOrgForm({ ...newOrgForm, OrganizationName: e.target.value })}
                                                placeholder="e.g. Pittsford Store"
                                                className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-slate-400 block mb-0.5">Organization Type</label>
                                            <select
                                                value={newOrgForm.OrganizationType}
                                                onChange={e => setNewOrgForm({ ...newOrgForm, OrganizationType: e.target.value })}
                                                className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                            >
                                                <option value="Store">Store</option>
                                                <option value="Franchise">Franchise</option>
                                                <option value="Corporate">Corporate</option>
                                                <option value="Internal">Internal</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex justify-end items-center gap-2 pt-1">
                                        <button
                                            type="button"
                                            onClick={() => setIsAddingOrg(false)}
                                            className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[11px] font-medium transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCreateOrganization}
                                            className="px-2.5 py-0.5 bg-purple-600 hover:bg-purple-500 text-white rounded text-[11px] font-semibold transition-colors"
                                        >
                                            Save Organization
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Service Instance Selector */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-slate-300 font-medium text-[11px]">Service Instance (SQL Server / Engine)</label>
                                    <div className="flex items-center gap-2">
                                        {selectedInstanceId && !isAddingInstance && !isEditingInstance && (
                                            <button
                                                type="button"
                                                onClick={handleStartEditInstance}
                                                className="text-[10px] text-sky-400 hover:text-sky-300 flex items-center gap-0.5 font-medium"
                                            >
                                                <span className="material-symbols-outlined text-[12px]">edit</span>
                                                Edit Selection
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (isAddingInstance || isEditingInstance) {
                                                    setIsAddingInstance(false);
                                                    setIsEditingInstance(false);
                                                } else {
                                                    setIsAddingInstance(true);
                                                    setIsEditingInstance(false);
                                                    setNewInstanceForm({ ServiceName: '', EngineType: 'SQL Server', ListenPort: 1433, OrganizationID: '' });
                                                }
                                            }}
                                            className="text-[10px] text-emerald-400 hover:text-emerald-300 flex items-center gap-0.5 font-medium"
                                        >
                                            <span className="material-symbols-outlined text-[12px]">{(isAddingInstance || isEditingInstance) ? 'remove' : 'add'}</span>
                                            {(isAddingInstance || isEditingInstance) ? 'Cancel' : '+ Add New Instance'}
                                        </button>
                                    </div>
                                </div>

                                {(isAddingInstance || isEditingInstance) ? (
                                    <div className="p-2.5 bg-slate-900/60 rounded border border-slate-800 space-y-2">
                                        <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                            <span className="text-[10px] font-bold text-emerald-300">
                                                {isEditingInstance ? 'Edit Service Instance' : 'Add New Service Instance'}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => { setIsAddingInstance(false); setIsEditingInstance(false); }}
                                                className="text-slate-400 hover:text-white text-xs"
                                            >
                                                &times;
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2">
                                            <div className="col-span-1">
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Service Name</label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={newInstanceForm.ServiceName}
                                                    onChange={e => setNewInstanceForm({ ...newInstanceForm, ServiceName: e.target.value })}
                                                    placeholder="e.g. PBSSQL"
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs font-mono"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Engine Type</label>
                                                <select
                                                    value={newInstanceForm.EngineType}
                                                    onChange={e => setNewInstanceForm({ ...newInstanceForm, EngineType: e.target.value })}
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                >
                                                    <option value="SQL Server">SQL Server</option>
                                                    <option value="REST_API">REST API / SaaS</option>
                                                    <option value="PostgreSQL">PostgreSQL</option>
                                                    <option value="MySQL">MySQL</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Listen Port</label>
                                                <input
                                                    type="number"
                                                    value={newInstanceForm.ListenPort}
                                                    onChange={e => setNewInstanceForm({ ...newInstanceForm, ListenPort: parseInt(e.target.value, 10) || 1433 })}
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs font-mono"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-slate-400 block mb-0.5">Organization Override (Optional)</label>
                                            <select
                                                value={newInstanceForm.OrganizationID}
                                                onChange={e => setNewInstanceForm({ ...newInstanceForm, OrganizationID: e.target.value })}
                                                className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                            >
                                                <option value="">(Inherit from Host)</option>
                                                {topology.organizations.map(o => (
                                                    <option key={o.OrganizationID} value={o.OrganizationID}>{o.OrganizationName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex justify-end items-center gap-2 pt-1">
                                            <button
                                                type="button"
                                                onClick={() => { setIsAddingInstance(false); setIsEditingInstance(false); }}
                                                className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[11px] font-medium transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={isEditingInstance ? handleUpdateInstance : handleCreateInstance}
                                                className="px-2.5 py-0.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-[11px] font-semibold transition-colors"
                                            >
                                                {isEditingInstance ? 'Update Instance' : 'Save Instance'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <select
                                        value={selectedInstanceId}
                                        onChange={e => {
                                            setSelectedInstanceId(e.target.value);
                                            setSelectedContainerId('');
                                        }}
                                        className="w-full bg-slate-900/90 border border-slate-700/80 rounded px-2 py-1.5 text-white text-xs font-mono"
                                    >
                                        <option value="">-- Select Service Instance --</option>
                                        {availableInstances.map(i => {
                                            const host = topology.hosts.find(h => h.HostID === i.HostID);
                                            return (
                                                <option key={i.InstanceID} value={i.InstanceID}>
                                                    {i.ServiceName} ({i.EngineType}:{i.ListenPort}) {host ? `@ ${host.DisplayName || host.HostName}` : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                )}
                            </div>

                            {/* Data Container Selector */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-slate-300 font-medium text-[11px]">Target Container (Database / Tenant)</label>
                                    <div className="flex items-center gap-2">
                                        {selectedContainerId && !isAddingContainer && !isEditingContainer && (
                                            <button
                                                type="button"
                                                onClick={handleStartEditContainer}
                                                className="text-[10px] text-sky-400 hover:text-sky-300 flex items-center gap-0.5 font-medium"
                                            >
                                                <span className="material-symbols-outlined text-[12px]">edit</span>
                                                Edit Selection
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (isAddingContainer || isEditingContainer) {
                                                    setIsAddingContainer(false);
                                                    setIsEditingContainer(false);
                                                } else {
                                                    setIsAddingContainer(true);
                                                    setIsEditingContainer(false);
                                                    setNewContainerForm({ ContainerName: '', ContainerType: 'RELATIONAL_DATABASE', ContainerRole: 'PRODUCTION', OrganizationID: '' });
                                                }
                                            }}
                                            className="text-[10px] text-emerald-400 hover:text-emerald-300 flex items-center gap-0.5 font-medium"
                                        >
                                            <span className="material-symbols-outlined text-[12px]">{(isAddingContainer || isEditingContainer) ? 'remove' : 'add'}</span>
                                            {(isAddingContainer || isEditingContainer) ? 'Cancel' : '+ Add New Container'}
                                        </button>
                                    </div>
                                </div>

                                {(isAddingContainer || isEditingContainer) ? (
                                    <div className="p-2.5 bg-slate-900/60 rounded border border-slate-800 space-y-2">
                                        <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                            <span className="text-[10px] font-bold text-emerald-300">
                                                {isEditingContainer ? 'Edit Data Container' : 'Add New Data Container'}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => { setIsAddingContainer(false); setIsEditingContainer(false); }}
                                                className="text-slate-400 hover:text-white text-xs"
                                            >
                                                &times;
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2">
                                            <div className="col-span-1">
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Container Name</label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={newContainerForm.ContainerName}
                                                    onChange={e => setNewContainerForm({ ...newContainerForm, ContainerName: e.target.value })}
                                                    placeholder="e.g. pbsdata00"
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs font-mono"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Container Type</label>
                                                <select
                                                    value={newContainerForm.ContainerType}
                                                    onChange={e => setNewContainerForm({ ...newContainerForm, ContainerType: e.target.value })}
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                >
                                                    <option value="RELATIONAL_DATABASE">Relational Database</option>
                                                    <option value="SAAS_TENANT">SaaS Tenant</option>
                                                    <option value="DOCUMENT_STORE">Document Store</option>
                                                    <option value="FILE_SYSTEM">File System</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Container Role</label>
                                                <select
                                                    value={newContainerForm.ContainerRole}
                                                    onChange={e => setNewContainerForm({ ...newContainerForm, ContainerRole: e.target.value })}
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                >
                                                    <option value="PRODUCTION">Production</option>
                                                    <option value="SANDBOX">Sandbox</option>
                                                    <option value="STAGING">Staging</option>
                                                    <option value="DEVELOPMENT">Development</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-slate-400 block mb-0.5">Organization Override (Optional)</label>
                                            <select
                                                value={newContainerForm.OrganizationID}
                                                onChange={e => setNewContainerForm({ ...newContainerForm, OrganizationID: e.target.value })}
                                                className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                            >
                                                <option value="">(Inherit from Instance / Host)</option>
                                                {topology.organizations.map(o => (
                                                    <option key={o.OrganizationID} value={o.OrganizationID}>{o.OrganizationName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex justify-end items-center gap-2 pt-1">
                                            <button
                                                type="button"
                                                onClick={() => { setIsAddingContainer(false); setIsEditingContainer(false); }}
                                                className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[11px] font-medium transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={isEditingContainer ? handleUpdateContainer : handleCreateContainer}
                                                className="px-2.5 py-0.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-[11px] font-semibold transition-colors"
                                            >
                                                {isEditingContainer ? 'Update Container' : 'Save Container'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <select
                                            value={selectedContainerId}
                                            onChange={e => setSelectedContainerId(e.target.value)}
                                            className="w-full bg-slate-900/90 border border-slate-700/80 rounded px-2 py-1.5 text-white text-xs font-mono"
                                        >
                                            <option value="">-- Select Data Container / Scope --</option>
                                            <option value="__INSTANCE__" className="text-amber-400 font-semibold">
                                                ⚡ (Assign to Instance Level - Inherited by all databases on this instance)
                                            </option>
                                            {availableContainers.map(c => {
                                                const inst = topology.instances.find(i => i.InstanceID === c.InstanceID);
                                                return (
                                                    <option key={c.ContainerID} value={c.ContainerID}>
                                                        {c.ContainerName} [{c.ContainerType || 'DB'}] {inst ? `(on ${inst.ServiceName})` : ''}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        {selectedContainerId === '__INSTANCE__' && (
                                            <div className="mt-1 px-2 py-1 bg-amber-950/40 border border-amber-800/50 rounded text-amber-200 text-[10px] flex items-center gap-1.5">
                                                <span className="material-symbols-outlined text-[13px]">info</span>
                                                <span>Instance Credential Binding: all databases on this service instance will automatically inherit these credentials.</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ========================================================================= */}
                        {/* SECTION 3: CREDENTIAL SELECTION & DYNAMIC AUTH FORM */}
                        {/* ========================================================================= */}
                        <div className="space-y-2.5">
                            <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                <h4 className="text-[11px] font-bold text-teal-400 flex items-center gap-1.5 uppercase tracking-wider">
                                    <span className="material-symbols-outlined text-[14px]">key</span>
                                    3. Security Credential
                                </h4>
                                {selectedCredObj && (
                                    <span className="text-[9px] text-emerald-400 bg-emerald-950/80 px-1.5 py-0.2 rounded border border-emerald-800/50 font-bold font-mono">
                                        LINKED #{selectedCredObj.CredentialID}
                                    </span>
                                )}
                            </div>

                            {/* Credential Dropdown Header & Selector */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-slate-300 font-medium text-[11px]">Security Credential</label>
                                    <div className="flex items-center gap-2">
                                        {selectedCredentialId && selectedCredentialId !== '__NEW__' && !isEditingCredential && (
                                            <button
                                                type="button"
                                                onClick={handleStartEditCredential}
                                                className="text-[10px] text-sky-400 hover:text-sky-300 flex items-center gap-0.5 font-medium"
                                            >
                                                <span className="material-symbols-outlined text-[12px]">edit</span>
                                                Edit Selection
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (selectedCredentialId === '__NEW__' || isEditingCredential) {
                                                    setSelectedCredentialId('');
                                                    setIsEditingCredential(false);
                                                } else {
                                                    setSelectedCredentialId('__NEW__');
                                                    setIsEditingCredential(false);
                                                }
                                            }}
                                            className="text-[10px] text-emerald-400 hover:text-emerald-300 flex items-center gap-0.5 font-medium"
                                        >
                                            <span className="material-symbols-outlined text-[12px]">{(selectedCredentialId === '__NEW__' || isEditingCredential) ? 'remove' : 'add'}</span>
                                            {(selectedCredentialId === '__NEW__' || isEditingCredential) ? 'Cancel' : '+ Add New Credential'}
                                        </button>
                                    </div>
                                </div>
                                <select
                                    value={selectedCredentialId}
                                    onChange={e => {
                                        const val = e.target.value;
                                        setSelectedCredentialId(val);
                                        if (val && val !== '__NEW__') {
                                            const cr = topology.credentials.find(c => String(c.CredentialID) === String(val));
                                            if (cr) {
                                                setNewCredForm(prev => ({
                                                    ...prev,
                                                    CredentialName: cr.CredentialName,
                                                    AuthModelID: cr.AuthModelID || 3,
                                                    Username: cr.Username || prev.Username
                                                }));
                                                if (!bindingName) setBindingName(cr.CredentialName);
                                            }
                                        }
                                    }}
                                    className="w-full bg-slate-900/90 border border-slate-700/80 rounded px-2 py-1.5 text-white text-xs"
                                >
                                    <option value="">-- Choose Existing Credential --</option>
                                    <option value="__NEW__" className="text-emerald-400 font-bold">+ [Add New Credential...]</option>
                                    {topology.credentials.map(c => {
                                        const am = topology.authModels.find(a => a.AuthModelID === c.AuthModelID);
                                        return (
                                            <option key={c.CredentialID} value={c.CredentialID}>
                                                {c.CredentialName} ({am?.AuthModelName || 'Login'}) {c.Username ? `[${c.Username}]` : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>

                            {/* Existing Credential Summary Bar (Flat & Compact) */}
                            {selectedCredObj && selectedCredentialId !== '__NEW__' && !isEditingCredential && (
                                <div className="px-2.5 py-1.5 bg-slate-900/50 rounded border border-slate-800 flex items-center justify-between text-xs">
                                    <div className="space-y-0.5">
                                        <div className="font-medium text-white flex items-center gap-1.5 text-xs">
                                            <span>{selectedCredObj.CredentialName}</span>
                                            <span className="text-[10px] text-slate-400">({activeAuthModel?.AuthModelName || 'SQL Login'})</span>
                                        </div>
                                        <div className="text-[10px] text-slate-400 font-mono">
                                            User/Account: <span className="text-slate-200">{selectedCredObj.Username || '(OAuth/Token)'}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={handleStartEditCredential}
                                            className="px-2 py-0.5 bg-sky-950 hover:bg-sky-900 border border-sky-800 text-[10px] text-sky-300 hover:text-white rounded flex items-center gap-1 font-medium transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-[12px]">edit</span>
                                            Edit Credential
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedCredentialId('__NEW__');
                                                setIsEditingCredential(false);
                                            }}
                                            className="text-[10px] text-teal-400 hover:text-teal-300 underline font-medium"
                                        >
                                            + New Instead
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Credential Form Subform (Only displays when creating new or editing) */}
                            {(selectedCredentialId === '__NEW__' || isEditingCredential) && (
                                <div className="space-y-2.5 pt-1 border-t border-slate-800/80 animate-fade-in">
                                    {isEditingCredential && (
                                        <div className="flex items-center justify-between px-2.5 py-1 bg-sky-950/40 border border-sky-800/50 rounded text-sky-200 text-xs">
                                            <span className="font-semibold flex items-center gap-1.5 text-[11px]">
                                                <span className="material-symbols-outlined text-[13px]">edit</span>
                                                Editing Credential #{selectedCredObj?.CredentialID}: {selectedCredObj?.CredentialName}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setIsEditingCredential(false)}
                                                className="text-slate-400 hover:text-white text-[10px] underline"
                                            >
                                                Cancel Edit
                                            </button>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[10px] text-slate-400 block mb-0.5">Credential Name</label>
                                            <input
                                                type="text"
                                                required={selectedCredentialId === '__NEW__'}
                                                value={newCredForm.CredentialName}
                                                onChange={e => setNewCredForm({ ...newCredForm, CredentialName: e.target.value })}
                                                placeholder="e.g. pbsdata00 sa Admin"
                                                className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-slate-400 block mb-0.5">System / Connection Type</label>
                                            <select
                                                value={newCredForm.SourceTypeID}
                                                onChange={e => {
                                                    const stid = parseInt(e.target.value, 10);
                                                    const dt = topology.driverTypes.find(d => d.SourceTypeID === stid);
                                                    setNewCredForm(prev => ({
                                                        ...prev,
                                                        SourceTypeID: stid,
                                                        AuthModelID: dt?.AuthModelID || prev.AuthModelID
                                                    }));
                                                }}
                                                className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                            >
                                                {topology.driverTypes.map(d => (
                                                    <option key={d.SourceTypeID} value={d.SourceTypeID}>{d.TypeName} ({d.TypeCode})</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Auth Model Selector & Add AuthModel Inline */}
                                    <div>
                                        <div className="flex items-center justify-between mb-0.5">
                                            <label className="text-[10px] text-slate-400">Authentication Model</label>
                                            <button
                                                type="button"
                                                onClick={() => setIsAddingAuthModel(!isAddingAuthModel)}
                                                className="text-[9px] text-teal-400 hover:underline"
                                            >
                                                {isAddingAuthModel ? 'Cancel' : '+ Add New AuthModel'}
                                            </button>
                                        </div>

                                        {isAddingAuthModel ? (
                                            <div className="p-2.5 bg-slate-900/80 rounded border border-teal-900/60 space-y-2 mb-2">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="text-[10px] text-slate-400 block mb-0.5">Code (e.g. BEARER)</label>
                                                        <input
                                                            type="text"
                                                            required
                                                            value={newAuthModelForm.AuthModelCode}
                                                            onChange={e => setNewAuthModelForm({ ...newAuthModelForm, AuthModelCode: e.target.value })}
                                                            className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono uppercase text-xs"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] text-slate-400 block mb-0.5">Name</label>
                                                        <input
                                                            type="text"
                                                            required
                                                            value={newAuthModelForm.AuthModelName}
                                                            onChange={e => setNewAuthModelForm({ ...newAuthModelForm, AuthModelName: e.target.value })}
                                                            className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex justify-end items-center gap-2 pt-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsAddingAuthModel(false)}
                                                        className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[11px] font-medium transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleCreateAuthModel}
                                                        className="px-2.5 py-0.5 bg-teal-600 hover:bg-teal-500 text-white rounded text-[11px] font-semibold transition-colors"
                                                    >
                                                        Save Auth Model
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <select
                                                value={newCredForm.AuthModelID}
                                                onChange={e => setNewCredForm({ ...newCredForm, AuthModelID: parseInt(e.target.value, 10) })}
                                                className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                            >
                                                {topology.authModels.map(am => (
                                                    <option key={am.AuthModelID} value={am.AuthModelID}>
                                                        {am.AuthModelName} ({am.AuthModelCode})
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>

                                    {/* OAuth2 / QuickBooks Fields */}
                                    {isOAuth && (
                                        <div className="space-y-2 p-2.5 bg-slate-900/50 rounded border border-slate-800">
                                            {/* Pre-fill / Link from Integrations */}
                                            {integrationAccounts.length > 0 && (
                                                <div>
                                                    <label className="text-[9px] text-emerald-400 font-bold block mb-1 uppercase tracking-wider">
                                                        Link Existing Account from Integrations
                                                    </label>
                                                    <select
                                                        onChange={async (e) => {
                                                            const aid = e.target.value;
                                                            if (!aid) return;
                                                            try {
                                                                const res = await fetch('/api/catalog/connections/link-integration-account', {
                                                                    method: 'POST',
                                                                    headers: authHeaders,
                                                                    body: JSON.stringify({ accountId: aid })
                                                                });
                                                                const d = await res.json();
                                                                if (d.success && d.data) {
                                                                    setNewCredForm(prev => ({
                                                                        ...prev,
                                                                        realmId: d.data.RealmID || prev.realmId,
                                                                        clientId: d.data.ClientID || prev.clientId,
                                                                        environment: d.data.Environment || 'sandbox',
                                                                        baseUrl: d.data.BaseURL || prev.baseUrl,
                                                                        CredentialName: prev.CredentialName || d.data.ConnectionName
                                                                    }));
                                                                }
                                                            } catch (err) {
                                                                console.error('Error prefilling integration:', err);
                                                            }
                                                        }}
                                                        className="w-full bg-slate-900 border border-emerald-700/60 rounded px-2 py-1 text-white text-xs"
                                                    >
                                                        <option value="">-- Select Integration Account to Pre-fill --</option>
                                                        {integrationAccounts.map(acc => (
                                                            <option key={acc.AccountID} value={acc.AccountID}>
                                                                {acc.AccountName} ({acc.Environment}) [Realm: {acc.RealmID || 'N/A'}]
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Company / Realm ID</label>
                                                    <input
                                                        type="text"
                                                        value={newCredForm.realmId}
                                                        onChange={e => setNewCredForm({ ...newCredForm, realmId: e.target.value })}
                                                        placeholder="e.g. 9341457812759223"
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono text-xs"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Environment</label>
                                                    <select
                                                        value={newCredForm.environment}
                                                        onChange={e => setNewCredForm({ ...newCredForm, environment: e.target.value })}
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                    >
                                                        <option value="sandbox">Sandbox</option>
                                                        <option value="production">Production</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">OAuth Client ID</label>
                                                    <input
                                                        type="text"
                                                        value={newCredForm.clientId}
                                                        onChange={e => setNewCredForm({ ...newCredForm, clientId: e.target.value })}
                                                        placeholder="OAuth Client ID"
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono text-xs"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Client Secret</label>
                                                    <input
                                                        type={showPassword ? 'text' : 'password'}
                                                        value={newCredForm.clientSecret}
                                                        onChange={e => setNewCredForm({ ...newCredForm, clientSecret: e.target.value })}
                                                        placeholder={editingConnection ? '(Unchanged)' : 'OAuth Client Secret'}
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono text-xs"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Base API URL</label>
                                                <input
                                                    type="text"
                                                    value={newCredForm.baseUrl}
                                                    onChange={e => setNewCredForm({ ...newCredForm, baseUrl: e.target.value })}
                                                    placeholder="https://sandbox-quickbooks.api.intuit.com"
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono text-[11px]"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Standard SQL Login Fields */}
                                    {isSqlLogin && (
                                        <div className="space-y-2 p-2.5 bg-slate-900/50 rounded border border-slate-800">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Username / Login</label>
                                                    <input
                                                        type="text"
                                                        required
                                                        value={newCredForm.Username}
                                                        onChange={e => setNewCredForm({ ...newCredForm, Username: e.target.value })}
                                                        placeholder="sa"
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono text-xs"
                                                    />
                                                </div>
                                                <div>
                                                    <div className="flex items-center justify-between mb-0.5">
                                                        <label className="text-[10px] text-slate-400">Password</label>
                                                        <button 
                                                            type="button" 
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            className="text-[9px] text-slate-500 hover:text-slate-300"
                                                        >
                                                            {showPassword ? 'Hide' : 'Show'}
                                                        </button>
                                                    </div>
                                                    <input
                                                        type={showPassword ? 'text' : 'password'}
                                                        value={newCredForm.password}
                                                        onChange={e => setNewCredForm({ ...newCredForm, password: e.target.value })}
                                                        placeholder={editingConnection ? '(Keep unchanged)' : 'Password'}
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono text-xs"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* API Key Fields */}
                                    {isApiKey && (
                                        <div className="space-y-2 p-2.5 bg-slate-900/50 rounded border border-slate-800">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">API Key</label>
                                                    <input
                                                        type="password"
                                                        value={newCredForm.apiKey}
                                                        onChange={e => setNewCredForm({ ...newCredForm, apiKey: e.target.value })}
                                                        placeholder="API Key Token"
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono text-xs"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Header Name</label>
                                                    <input
                                                        type="text"
                                                        value={newCredForm.headerName}
                                                        onChange={e => setNewCredForm({ ...newCredForm, headerName: e.target.value })}
                                                        placeholder="Authorization"
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono text-xs"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Windows Auth Fields */}
                                    {isWindowsAuth && (
                                        <div className="space-y-2 p-2.5 bg-slate-900/50 rounded border border-slate-800">
                                            <div className="grid grid-cols-3 gap-2">
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Domain (Optional)</label>
                                                    <input
                                                        type="text"
                                                        value={newCredForm.domain}
                                                        onChange={e => setNewCredForm({ ...newCredForm, domain: e.target.value })}
                                                        placeholder="e.g. WORKGROUP"
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono text-xs"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Username</label>
                                                    <input
                                                        type="text"
                                                        required
                                                        value={newCredForm.Username}
                                                        onChange={e => setNewCredForm({ ...newCredForm, Username: e.target.value })}
                                                        placeholder="Username"
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono text-xs"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Password</label>
                                                    <input
                                                        type="password"
                                                        value={newCredForm.password}
                                                        onChange={e => setNewCredForm({ ...newCredForm, password: e.target.value })}
                                                        placeholder="Password"
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white font-mono text-xs"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action buttons inside Credential subform */}
                                    {isEditingCredential && (
                                        <div className="flex justify-end items-center gap-2 pt-1 border-t border-slate-800/80">
                                            <button
                                                type="button"
                                                onClick={() => setIsEditingCredential(false)}
                                                className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[11px] font-medium transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleUpdateCredential}
                                                className="px-2.5 py-0.5 bg-sky-600 hover:bg-sky-500 text-white rounded text-[11px] font-semibold transition-colors"
                                            >
                                                Update Credential
                                            </button>
                                        </div>
                                    )}
                                    {selectedCredentialId === '__NEW__' && (
                                        <div className="flex justify-end items-center gap-2 pt-1 border-t border-slate-800/80">
                                            <button
                                                type="button"
                                                onClick={() => setSelectedCredentialId('')}
                                                className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[11px] font-medium transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleCreateCredential}
                                                className="px-2.5 py-0.5 bg-teal-600 hover:bg-teal-500 text-white rounded text-[11px] font-semibold transition-colors"
                                            >
                                                Save Credential
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Test Result Display */}
                        {testResult && (
                            <div className={`p-2 rounded border text-xs animate-fade-in ${
                                testResult.success ? 'bg-emerald-950/80 border-emerald-800 text-emerald-300' : 'bg-red-950/80 border-red-800 text-red-300'
                            }`}>
                                <div className="flex items-center gap-1.5 font-bold text-xs mb-0.5">
                                    <span className="material-symbols-outlined text-[14px]">
                                        {testResult.success ? 'check_circle' : 'error'}
                                    </span>
                                    <span>{testResult.success ? 'Connection Successful!' : 'Connection Failed'}</span>
                                    {testResult.latencyMs && (
                                        <span className="text-[10px] font-mono text-slate-400">({testResult.latencyMs}ms)</span>
                                    )}
                                </div>
                                <div className="text-[11px] font-mono whitespace-pre-wrap">{testResult.message || testResult.error}</div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="pt-2.5 mt-auto flex items-center justify-between gap-2 border-t border-slate-800 sticky bottom-0 bg-slate-950/95 py-2">
                            <button
                                type="button"
                                disabled={testingConnection}
                                onClick={handleTest}
                                className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                            >
                                <span className={`material-symbols-outlined text-[14px] ${testingConnection ? 'animate-spin' : ''}`}>
                                    {testingConnection ? 'progress_activity' : 'network_check'}
                                </span>
                                {testingConnection ? 'Testing...' : 'Test Connection'}
                            </button>

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-3 py-1 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded text-xs font-semibold transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-3.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all disabled:opacity-50"
                                >
                                    <span className="material-symbols-outlined text-[15px]">
                                        {saving ? 'progress_activity' : 'save'}
                                    </span>
                                    {saving ? 'Saving...' : (editingConnection ? 'Save Changes' : 'Create & Bind')}
                                </button>
                            </div>
                        </div>

                    </form>
                )}
            </div>
        </div>
    );
}
